import React from "react";
import {
  Box, Chip, IconButton, Paper, Typography, alpha, Grid,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import { useGetSystemHealthQuery } from "../../../Api/attoDeskApi";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import {
  NAVY, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  ok:       { bg: "#dcfce7", fg: "#166534" },
  true:     { bg: "#dcfce7", fg: "#166534" },
  warning:  { bg: "#fef3c7", fg: "#92400e" },
  unknown:  { bg: "#f1f5f9", fg: "#475569" },
  disabled: { bg: "#f1f5f9", fg: "#475569" },
  down:     { bg: "#fee2e2", fg: "#991b1b" },
  false:    { bg: "#fee2e2", fg: "#991b1b" },
};

const Pill: React.FC<{ value: any }> = ({ value }) => {
  const key = String(value).toLowerCase();
  const s = STATUS_COLOR[key] || STATUS_COLOR.unknown;
  const label = value === true ? "YES" : value === false ? "NO" : String(value).toUpperCase();
  return (
    <Chip size="small" label={label}
      sx={{ height: 22, fontSize: 11, fontWeight: 700,
        bgcolor: s.bg, color: s.fg, fontFamily: "monospace" }} />
  );
};

const HealthPage: React.FC = () => {
  const isSuper = useAppSelector(selectIsSuperAdmin);
  const { data, isLoading, refetch, isFetching } = useGetSystemHealthQuery(undefined, {
    skip: !isSuper, pollingInterval: 30_000,
  });

  if (!isSuper) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
          System diagnostics are visible to Super Admins only.
        </Typography>
      </Box>
    );
  }

  if (isLoading) return <PageLoader height="20vh" />;

  const d = (data as any) || {};
  const rows: Array<[string, any]> = [
    ["API", d.api],
    ["Database", d.database],
    ["Storage Symlink", d.storage_link],
    ["Storage Writable", d.storage_writable],
    ["Media Path Writable", d.media_path_writable],
    ["Screenshot Path Writable", d.screenshot_path_writable],
    ["Release Path Writable", d.release_path_writable],
    ["Queue", d.queue],
    ["Scheduler", d.scheduler],
    ["Latest Android Release", d.latest_android_release ?? "none"],
    ["Latest Web Release", d.latest_web_release ?? "none"],
    ["APP_URL", d.app_url ?? "(empty)"],
    ["APP_ENV", d.app_env ?? "(empty)"],
    ["PHP Version", d.php_version],
    ["Laravel Version", d.laravel_version],
    ["Server Time", d.server_time],
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${alpha("#10b981", 0.18)} 0%, ${alpha("#10b981", 0.06)} 100%)`,
          color: "#10b981",
        }}>
          <MonitorHeartIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
            System Health
          </Typography>
          <Typography fontSize={12} color={TEXT_SECONDARY}>
            Auto-refreshes every 30s. {isFetching ? "Refreshing…" : ""}
          </Typography>
        </Box>
        <IconButton onClick={() => refetch()}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, width: 38, height: 38, bgcolor: "#fff" }}>
          <RefreshIcon sx={{ fontSize: 18, color: NAVY }} />
        </IconButton>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        <Grid container>
          {rows.map(([label, value], i) => (
            <Grid item xs={12} md={6} key={String(label)}
              sx={{
                p: 2.5,
                borderBottom: `1px solid ${BORDER}`,
                borderRight: { md: i % 2 === 0 ? `1px solid ${BORDER}` : "none" },
                display: "flex", alignItems: "center", gap: 2,
              }}>
              <Typography sx={{
                fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                textTransform: "uppercase", letterSpacing: "0.06em",
                minWidth: 200,
              }}>
                {label}
              </Typography>
              <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                {typeof value === "boolean" || ["ok","warning","down","disabled","unknown"].includes(String(value))
                  ? <Pill value={value} />
                  : <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY,
                      fontFamily: "monospace", wordBreak: "break-all", textAlign: "right" }}>
                      {value ?? "—"}
                    </Typography>}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};

export default HealthPage;
